import { FC, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  Brush,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  BAR_COLOR_DEFAULT,
  BarChartProps,
  BASELINE_COLOR_DEFAULT,
  GRID_COLOR_DEFAULT,
  isStackedBarFormatArray,
  isSingleValueBarFormatArray,
  LABEL_Y_COLOR_DEFAULT,
  MIN_BRUSH_DEFAULT_LENGTH,
  StackedBarFormatColors,
} from './BarChart.types';
import { StyledChartContainer } from './BarChart.styles';
import { useTranslation } from 'react-i18next';
import { COMMON_TRANSLATION } from '@/common/constants';
import { DEFAULT_MIN_MAX_Y_AXIS_DOMAIN } from '@/modules/shared/constants';
import {
  changeMultipleColumnToReChartStackedFormat,
  changeMultipleColumnToSingleColumnArray,
} from './helpers/dataTransform';
import { BaseLineReferenceLabel } from './components/BaselineReferenceLabel';
import { formatTickY, getDomain, renderTickX } from './helpers/componentRender';
import { SingleValueTooltip } from './components/SingleValueTooltip';
import { MultipleValueTooltip } from './components/MultipleValueTooltip';

const ChartBar: FC<BarChartProps> = ({
  baseline,
  baselinePeriod,
  chartProps,
  colors,
  data,
  minData = MIN_BRUSH_DEFAULT_LENGTH,
  startAtEnd,
  tickY,
  tickX,
  tooltip,
  tooltipVariant,
  xAxisKey = 'groupValue',
  dataKey = 'value',
  stackedColors = [],
  stackedBarChart,
}) => {
  const { t } = useTranslation(COMMON_TRANSLATION);
  const [barGraphData, setBarGraphData] = useState<any>({});
  const [isTooltipActive, setIsTooltipActive] = useState<boolean>(false);
  const [tooltipWidth, setTooltipWidth] = useState<number>(0);

  const { isSingleValueBarFormat, isStackedBarFormat, internalData, maxYAxisDomain, formattedBarChartData } =
    useMemo(() => {
      const isSingleValueBarFormat = isSingleValueBarFormatArray(data);
      const internalData = isSingleValueBarFormat ? data : changeMultipleColumnToSingleColumnArray(data);
      const maxDomain = Math.max(...internalData.map((i) => i.value), baseline ?? 0, DEFAULT_MIN_MAX_Y_AXIS_DOMAIN);
      return {
        isSingleValueBarFormat,
        isStackedBarFormat: isStackedBarFormatArray(data),
        internalData,
        maxYAxisDomain: Math.ceil(maxDomain / 10) * 10,
        formattedBarChartData: isSingleValueBarFormat ? data : changeMultipleColumnToReChartStackedFormat(data),
      };
    }, [data, baseline]);

  const currentStackedCampaignName = (() => {
    if (isStackedBarFormat && barGraphData?.tooltipPayload && barGraphData?.tooltipPayload[0]) {
      const dataKey = barGraphData?.tooltipPayload[0]?.dataKey;
      const campaignData = stackedColors.find((d: StackedBarFormatColors) => d.id === dataKey);
      return campaignData?.name ?? '';
    }
    return '';
  })();

  return (
    <StyledChartContainer>
      <BaseLineReferenceLabel baseline={baseline} baselinePeriod={baselinePeriod} t={t} />
      <ResponsiveContainer
        height={'100%'}
        minHeight={chartProps?.minHeight ? chartProps.minHeight : 315}
        minWidth={chartProps?.minWidth ? chartProps.minWidth : ''}
      >
        <BarChart
          data={formattedBarChartData}
          margin={{
            bottom: 0,
            right: 20,
            top: 10,
          }}
        >
          <XAxis
            axisLine={false}
            data-testid={'x-axis'}
            dataKey={xAxisKey}
            height={40}
            interval={0}
            style={{
              width: '200',
            }}
            tick={(tickValues) => renderTickX({ ...tickValues, tickX })}
            tickCount={7}
            tickLine={false}
          />
          <YAxis
            allowDataOverflow={true}
            axisLine={false}
            domain={getDomain(maxYAxisDomain, tickY)}
            tick={{
              fill: colors?.labelY ?? LABEL_Y_COLOR_DEFAULT,
              fontSize: 12,
              fontWeight: 500,
            }}
            tickCount={7}
            tickFormatter={(values) => formatTickY(values, tickY)}
            tickLine={false}
            textAnchor="end"
          />
          {baseline && (
            <ReferenceLine
              stroke={colors?.baseline ?? BASELINE_COLOR_DEFAULT}
              strokeDasharray="8 9"
              strokeWidth="2"
              y={baseline}
            />
          )}
          <CartesianGrid stroke={colors?.grid ?? GRID_COLOR_DEFAULT} vertical={false} />
          {isSingleValueBarFormat && (
            <>
              <Bar
                barSize={18}
                dataKey={dataKey}
                fill={colors?.bar ?? BAR_COLOR_DEFAULT}
                minPointSize={1}
                radius={[2, 2, 0, 0]}
                stackId={dataKey}
                onMouseLeave={() => setIsTooltipActive(false)}
                onMouseEnter={(internalData) => {
                  setBarGraphData(internalData);
                  setIsTooltipActive(true);
                }}
              />
              <Tooltip
                content={
                  <SingleValueTooltip
                    tooltip={tooltip}
                    tooltipVariant={tooltipVariant}
                    isTooltipActive={isTooltipActive}
                    payload={barGraphData}
                    onChangeTooltipWidth={(width: number) => {
                      setTooltipWidth(width);
                    }}
                  />
                }
                cursor={{ fill: 'transparent' }}
                position={{ x: barGraphData.x - tooltipWidth / 2.2, y: barGraphData.y - 90 }}
                wrapperStyle={{ outline: 'none', textAlign: 'center' }}
              />
            </>
          )}
          {isStackedBarFormat && (
            <>
              {stackedColors.map((item, index) => {
                return (
                  <Bar
                    barSize={18}
                    dataKey={item.id}
                    fill={item.color}
                    key={index}
                    radius={[2, 2, 0, 0]}
                    stackId={dataKey}
                    onMouseLeave={() => setIsTooltipActive(false)}
                    onMouseEnter={(enterData) => {
                      setBarGraphData(enterData);
                      setIsTooltipActive(true);
                    }}
                  />
                );
              })}
              <Tooltip
                content={
                  <MultipleValueTooltip
                    tooltip={currentStackedCampaignName}
                    backgroundColor={stackedBarChart?.tooltipBackgroundColor}
                    isTooltipActive={isTooltipActive}
                    data={barGraphData}
                    onChangeTooltipWidth={(width: number) => {
                      setTooltipWidth(width);
                    }}
                    tooltipEndValue={stackedBarChart?.tooltipEndValue}
                  />
                }
                cursor={{ fill: 'transparent' }}
                position={{ x: barGraphData.x + 20, y: barGraphData?.tooltipPosition?.y ?? 0 }}
                wrapperStyle={{ outline: 'none', textAlign: 'center' }}
              />
            </>
          )}
          {internalData.length > minData && startAtEnd && (
            <Brush startIndex={internalData.length - minData} height={10} />
          )}
          {internalData.length > minData && !startAtEnd && <Brush endIndex={minData} height={10} />}
        </BarChart>
      </ResponsiveContainer>
    </StyledChartContainer>
  );
};

export default ChartBar;
